import React from "react";
import { motion } from "framer-motion";
import {
  ScanSearch,
  Workflow,
  Cpu,
  ShieldCheck,
  Rocket,
  CheckCircle2,
} from "lucide-react";
const STEPS = [
  {
    num: "01",
    phase: "DISCOVERY & AS-IS AUDIT",
    title: "Automated Legacy Extraction",
    desc: "Using OrchestAI discovery crawlers, we ingest existing BPM diagrams, business logic rules, and legacy code (Pega, IBM BPM, Appian) into a structured inventory.",
    details: [
      "Rule & gateway dependency mapping",
      "Automated complexity categorization",
      "Legacy tech debt assessment",
    ],
    icon: ScanSearch,
  },
  {
    num: "02",
    phase: "STANDARDIZATION",
    title: "BPMN 2.0 & DMN Modeling",
    desc: "Architecting cloud-native workflow schemas aligned strictly with ISO BPMN 2.0 and DMN standards, eliminating vendor lock-in and spaghetti code.",
    details: [
      "Decoupled human & service tasks",
      "DMN decision table optimization",
      "Microservices orchestration design",
    ],
    icon: Workflow,
  },
  {
    num: "03",
    phase: "ENGINEERING ACCELERATION",
    title: "Zeebe External Worker Scaffolding",
    desc: "Auto-generating Spring Boot external workers, gRPC clients, and enterprise connectors via OrchestAI to accelerate implementation by up to 50%.",
    details: [
      "Spring Boot 3.x & Java worker generation",
      "FHIR R4 & REST connector binding",
      "Idempotent job worker patterns",
    ],
    icon: Cpu,
  },
  {
    num: "04",
    phase: "QUALITY & GUARDRAILS",
    title: "Automated BPMN Linting & AI Governance",
    desc: "Enforcing Truviq quality guardrails with automated linter routines to validate transaction boundaries, gateway rules, and error handling policies.",
    details: [
      "Automated anti-pattern validation",
      "Chaos & load test simulation",
      "Security token boundary audits",
    ],
    icon: ShieldCheck,
  },
  {
    num: "05",
    phase: "DEPLOYMENT & OPERATIONS",
    title: "Zero-Downtime Production Cutover",
    desc: "Blue-green deployment to Camunda 8 SaaS or Self-Managed clusters with synchronized runtime state migration and 24/7 SLA telemetry.",
    details: [
      "Zero data-loss in-flight migration",
      "Elastic Zeebe broker tuning",
      "Real-time Prometheus/Grafana monitors",
    ],
    icon: Rocket,
  },
];
function StepCard({ step, isLeft }) {
  return (
    <motion.div
      initial={{ opacity: 0, x: isLeft ? -30 : 30 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
      className="bg-white border relative border-slate-300/80 rounded-xl p-6 sm:p-7 overflow-hidden shadow-[0_15px_35px_rgba(0,0,0,0.03)]"
    >
      <div
        className="pointer-events-none absolute top-0 left-0 w-full h-full opacity-20 "
        style={{
          background:
            "linear-gradient(135deg, #62d4f7 0%, #5dc9f0 40%, #3579be 60%",
        }}
      />
      <div className="flex flex-wrap items-center justify-between gap-2 pb-3 mb-3 border-b border-slate-100">
        <span className="font-mono text-[11px] font-bold uppercase tracking-[0.15em] text-slate-500">
          {step.phase}
        </span>
        <span className="font-mono text-[11px] font-bold text-slate-400">
          STEP // {step.num}
        </span>
      </div>
      <h3 className="font-display text-xl font-bold text-[#000036] tracking-tight mb-2">
        {step.title}
      </h3>
      <p className="text-sm sm:text-base text-[#000036] leading-relaxed mb-4">
        {step.desc}
      </p>
      <div className="flex flex-wrap gap-2 pt-3 border-t border-slate-100">
        {step.details.map((item) => (
          <div
            key={item}
            className="flex items-center gap-2 text-sm font-semibold text-slate-500"
          >
            <CheckCircle2 size={15} className="text-[#000036]" />
            <span>{item}</span>
          </div>
        ))}
      </div>
    </motion.div>
  );
}

export default function CamundaSteps() {
  return (
    <section className="relative w-full bg-[#ffffff] py-20 sm:py-24 px-3 sm:px-8 lg:px-12 overflow-hidden">
      <div className="3xl:w-4/5 xl:w-[90%] mx-auto relative z-10">
        <div className="flex flex-col items-center text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full backdrop-blur-md shadow-xs mb-3"
          >
            <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-600">
              Delivery Blueprint
            </span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="font-display text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-[#000036]"
          >
            Our Implementation{" "}
            <span className="italic font-serif font-normal text-[#5dc9f0]">
              Process
            </span>{" "}
          </motion.h2>
        </div>
        <div className="relative">
          <div className="absolute top-6 bottom-6 left-6 lg:left-1/2 -translate-x-1/2 w-px bg-slate-300 z-0" />
          <div className="space-y-12 lg:space-y-20">
            {STEPS.map((step, idx) => {
              const Icon = step.icon;
              const isLeft = idx % 2 === 0;
              return (
                <div
                  key={step.num}
                  className="relative flex items-start gap-2 lg:grid lg:grid-cols-[1fr_auto_1fr] md:gap-x-10 md:items-start"
                >
                  <div className="hidden lg:block">
                    {isLeft && <StepCard step={step} isLeft={true} />}
                  </div>
                  <div className="relative z-10 shrink-0">
                    <motion.div
                      initial={{ opacity: 0, scale: 0.6 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true, amount: 0.6 }}
                      transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                      className="w-12 h-12 rounded-full bg-[#000036] text-white flex items-center justify-center shadow-lg shadow-slate-950/20 ring-4 ring-[#eeeeee]"
                    >
                      <Icon className="w-5 h-5" />
                    </motion.div>
                  </div>
                  <div className="flex-1 lg:block">
                    <div className="lg:hidden">
                      <StepCard step={step} isLeft={false} />
                    </div>
                    <div className="hidden lg:block">
                      {!isLeft && <StepCard step={step} isLeft={false} />}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
